import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ColegioService from '../services/ColegioService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import { useAuth } from '../contexts/AuthContext';
import cursoService from '../services/CursoService';
import usuarioService from '../services/UsuarioService';
import aulaService from '../services/AulaService';
import { ADMIN_DASHBOARD_MENU_ITEMS, navigateAdminMenuItem, isFeatureEnabled } from '../utils/adminMenu';
import LoadingSpinner from './common/LoadingSpinner';
import SearchableSelect from './common/SearchableSelect';

const hasAgentesClasicosEnabled = (flags) =>
  isFeatureEnabled(flags, 'PermiteAgentesClasicosGPT');

const normalizeListResponse = (response) => (
  Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : []
);

const normalizeIdList = (value) => (
  Array.isArray(value)
    ? value.map((item) => (item == null ? '' : String(item))).filter(Boolean)
    : []
);

const formatCourseLabel = (curso) => (
  curso?.nombre
  || curso?.Nombre
  || `${curso?.valorAnio || curso?.ValorAnio || '—'}° ${curso?.division || curso?.Division || '—'} - ${curso?.cicloLectivo || curso?.CicloLectivo || '—'}`
);

const getMateriaId = (materia) => String(materia?.id || materia?.Id || '');

const getMateriaCourseIds = (materia) => normalizeIdList(materia?.cursoIds || materia?.CursoIds);

const formatMateriaLabel = (materia) => {
  const nombre = materia?.nombre || materia?.Nombre || 'Materia';
  const codigo = materia?.codigo || materia?.Codigo || '';
  return codigo ? `${nombre} · ${codigo}` : nombre;
};

const AdminCreateAula = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const stateColegioId = location.state?.idColegio || null;
  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => stateColegioId || ColegioService.getActiveColegioId(), [stateColegioId]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const hasAgentesClasicos = useMemo(() => hasAgentesClasicosEnabled(featureFlags), [featureFlags]);

  const [cursos, setCursos] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [classicAssistants, setClassicAssistants] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoadingMaterias, setIsLoadingMaterias] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    codigoSuscripcion: '',
    cursoIds: [],
    materiaIds: [],
    idsAsistentes: [],
    idProfesor: '',
    aTodosLosCursos: false,
    aTodasLasMaterias: false,
    cicloLectivo: currentYear,
    idColegio: activeColegioId || ''
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!activeColegioId) return;

      setIsLoadingData(true);
      try {
        const requests = [
          cursoService.getCursosActivos({ idColegio: activeColegioId }),
          usuarioService.getProfesoresPorColegio(activeColegioId)
        ];

        if (hasAgentesClasicos) {
          requests.push(aulaService.getAsistentesGptClasicosParaAula({ idColegio: activeColegioId }));
        }

        const [cursosRes, profesoresRes, assistantsRes = []] = await Promise.all(requests);
        setCursos(normalizeListResponse(cursosRes));
        setProfesores(Array.isArray(profesoresRes) ? profesoresRes : []);
        setClassicAssistants(Array.isArray(assistantsRes) ? assistantsRes : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar los datos necesarios.');
        setMessageType('error');
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();

    if (activeColegioId && !form.idColegio) {
      setForm((prev) => ({ ...prev, idColegio: activeColegioId }));
    }
  }, [activeColegioId, form.idColegio, hasAgentesClasicos]);

  const selectedCycle = Number(form.cicloLectivo) || currentYear;

  const availableCourses = useMemo(() => (
    cursos
      .filter((curso) => Number(curso?.cicloLectivo || curso?.CicloLectivo || 0) === selectedCycle)
      .sort((a, b) => formatCourseLabel(a).localeCompare(formatCourseLabel(b), 'es', { sensitivity: 'base' }))
  ), [cursos, selectedCycle]);

  const availableCourseIds = useMemo(() => (
    availableCourses
      .map((curso) => String(curso?.id || curso?.Id || ''))
      .filter(Boolean)
  ), [availableCourses]);

  const selectedCourseIdsKey = useMemo(() => (
    [...form.cursoIds].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' })).join('|')
  ), [form.cursoIds]);

  useEffect(() => {
    if (!activeColegioId) return;

    if (form.cursoIds.length === 0) {
      setMaterias([]);
      setForm((prev) => (
        prev.materiaIds.length === 0 && !prev.aTodasLasMaterias
          ? prev
          : { ...prev, materiaIds: [], aTodasLasMaterias: false }
      ));
      return;
    }

    let cancelled = false;

    const fetchMaterias = async () => {
      setIsLoadingMaterias(true);
      try {
        const list = await aulaService.getMateriasSeleccionablesParaAula({
          idColegio: activeColegioId,
          cursoIds: form.cursoIds,
          cicloLectivo: selectedCycle
        });

        if (cancelled) return;

        const items = Array.isArray(list) ? list : [];
        setMaterias(items);

        setForm((prev) => {
          const nextAvailableIds = items
            .map((materia) => getMateriaId(materia))
            .filter(Boolean);
          const nextAvailableSet = new Set(nextAvailableIds);
          const nextMateriaIds = prev.aTodasLasMaterias
            ? nextAvailableIds
            : prev.materiaIds.filter((id) => nextAvailableSet.has(String(id)));

          return {
            ...prev,
            materiaIds: nextMateriaIds,
            aTodasLasMaterias: prev.aTodasLasMaterias && nextAvailableIds.length > 0
          };
        });
      } catch (error) {
        if (cancelled) return;

        setMaterias([]);
        setForm((prev) => ({ ...prev, materiaIds: [], aTodasLasMaterias: false }));
        setMessage(error.message || 'No pudimos cargar las materias disponibles.');
        setMessageType('error');
      } finally {
        if (!cancelled) {
          setIsLoadingMaterias(false);
        }
      }
    };

    fetchMaterias();

    return () => {
      cancelled = true;
    };
  }, [activeColegioId, form.cursoIds, selectedCourseIdsKey, selectedCycle]);

  const cursoOptions = useMemo(() => (
    availableCourses
      .map((curso) => {
        const id = String(curso?.id || curso?.Id || '');
        const nombre = formatCourseLabel(curso);
        const cicloLectivo = curso?.cicloLectivo || curso?.CicloLectivo || '';
        const valorAnio = curso?.valorAnio || curso?.ValorAnio || '';
        const division = curso?.division || curso?.Division || '';
        const secondary = [valorAnio ? `${valorAnio}°` : '', division ? `División ${division}` : '', cicloLectivo ? `Ciclo ${cicloLectivo}` : '']
          .filter(Boolean)
          .join(' · ');

        return {
          value: id,
          label: nombre,
          secondary,
          searchText: `${nombre} ${valorAnio} ${division} ${cicloLectivo}`.trim()
        };
      })
      .filter((option) => option.value)
  ), [availableCourses]);

  const availableMateriaIds = useMemo(() => (
    materias
      .map((materia) => getMateriaId(materia))
      .filter(Boolean)
  ), [materias]);

  const materiaOptions = useMemo(() => (
    materias
      .map((materia) => {
        const id = getMateriaId(materia);
        const label = formatMateriaLabel(materia);
        const nombreArea = materia?.nombreArea || materia?.NombreArea || '';
        const cursosMateria = Array.isArray(materia?.cursos || materia?.Cursos)
          ? (materia?.cursos || materia?.Cursos)
          : [];
        const secondary = [nombreArea, cursosMateria.join(' · ')]
          .filter(Boolean)
          .join(' · ');

        return {
          value: id,
          label,
          secondary,
          searchText: `${label} ${nombreArea} ${cursosMateria.join(' ')}`.trim()
        };
      })
      .filter((option) => option.value)
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ), [materias]);

  const profesorOptions = useMemo(() => (
    (Array.isArray(profesores) ? profesores : [])
      .map((profesor) => {
        const id = String(profesor?.id || profesor?.Id || '');
        const displayName = profesor?.displayName || profesor?.DisplayName || '';
        const email = profesor?.email || profesor?.Email || '';
        const label = displayName || email || 'Profesor';

        return {
          value: id,
          label,
          secondary: email && email !== label ? email : '',
          searchText: `${label} ${email}`.trim()
        };
      })
      .filter((option) => option.value)
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ), [profesores]);

  const assistantOptions = useMemo(() => (
    classicAssistants
      .map((assistant) => {
        const id = String(assistant?.idOpenAIAssistant || assistant?.IdOpenAIAssistant || '');
        const nombreVisible = assistant?.nombreVisible || assistant?.NombreVisible || assistant?.nombre || assistant?.Nombre || 'Asistente IA';
        const nombreMateria = assistant?.nombreMateria || assistant?.NombreMateria || '';
        const nombreArea = assistant?.nombreArea || assistant?.NombreArea || '';
        const secondary = [nombreMateria, nombreArea]
          .filter(Boolean)
          .join(' · ');

        return {
          value: id,
          label: nombreVisible,
          secondary,
          searchText: `${nombreVisible} ${nombreMateria} ${nombreArea}`.trim()
        };
      })
      .filter((option) => option.value)
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ), [classicAssistants]);

  const selectedCourseSet = useMemo(() => new Set(form.cursoIds.map(String)), [form.cursoIds]);
  const selectedMateriaSet = useMemo(() => new Set(form.materiaIds.map(String)), [form.materiaIds]);

  const plannedPairs = useMemo(() => {
    const pairs = [];

    materias.forEach((materia) => {
      const materiaId = getMateriaId(materia);
      if (!selectedMateriaSet.has(materiaId)) {
        return;
      }

      getMateriaCourseIds(materia).forEach((courseId) => {
        if (selectedCourseSet.has(courseId)) {
          pairs.push({ courseId, materiaId });
        }
      });
    });

    return pairs;
  }, [materias, selectedCourseSet, selectedMateriaSet]);

  const coursesWithMultipleMaterias = useMemo(() => {
    const counts = new Map();
    plannedPairs.forEach((pair) => {
      counts.set(pair.courseId, (counts.get(pair.courseId) || 0) + 1);
    });

    return availableCourses
      .filter((curso) => counts.get(String(curso?.id || curso?.Id || '')) > 1)
      .map((curso) => formatCourseLabel(curso));
  }, [availableCourses, plannedPairs]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCycleChange = (value) => {
    const nextCycle = Number(value) || currentYear;
    const nextAvailableIds = cursos
      .filter((curso) => Number(curso?.cicloLectivo || curso?.CicloLectivo || 0) === nextCycle)
      .map((curso) => String(curso?.id || curso?.Id || ''))
      .filter(Boolean);
    const nextAvailableSet = new Set(nextAvailableIds);

    setForm((prev) => {
      const nextCursoIds = prev.aTodosLosCursos
        ? nextAvailableIds
        : prev.cursoIds.filter((id) => nextAvailableSet.has(String(id)));

      return {
        ...prev,
        cicloLectivo: value,
        cursoIds: nextCursoIds,
        aTodosLosCursos: prev.aTodosLosCursos && nextAvailableIds.length > 0
      };
    });
  };

  const handleCursosChange = (cursoIds) => {
    const normalizedIds = normalizeIdList(cursoIds);
    setForm((prev) => ({
      ...prev,
      cursoIds: normalizedIds,
      aTodosLosCursos: availableCourseIds.length > 0 && normalizedIds.length === availableCourseIds.length
    }));
  };

  const handleToggleAllCourses = (checked) => {
    if (checked && availableCourseIds.length === 0) {
      setMessage(`No hay cursos activos para el ciclo lectivo ${selectedCycle}.`);
      setMessageType('error');
      return;
    }

    setForm((prev) => ({
      ...prev,
      aTodosLosCursos: checked,
      cursoIds: checked ? availableCourseIds : []
    }));
  };

  const handleMateriasChange = (materiaIds) => {
    const normalizedIds = normalizeIdList(materiaIds);
    setForm((prev) => ({
      ...prev,
      materiaIds: normalizedIds,
      aTodasLasMaterias: availableMateriaIds.length > 0 && normalizedIds.length === availableMateriaIds.length
    }));
  };

  const handleToggleAllMaterias = (checked) => {
    if (checked && availableMateriaIds.length === 0) {
      setMessage('No hay materias disponibles para los cursos seleccionados.');
      setMessageType('error');
      return;
    }

    setForm((prev) => ({
      ...prev,
      aTodasLasMaterias: checked,
      materiaIds: checked ? availableMateriaIds : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del aula.');
      setMessageType('error');
      return;
    }
    if (!form.idProfesor) {
      setMessage('Selecciona el profesor responsable.');
      setMessageType('error');
      return;
    }
    if (!form.cursoIds.length) {
      setMessage('Selecciona al menos un curso para crear el aula.');
      setMessageType('error');
      return;
    }
    if (!form.materiaIds.length) {
      setMessage('Selecciona al menos una materia para crear el aula.');
      setMessageType('error');
      return;
    }
    if (!plannedPairs.length) {
      setMessage('La combinación actual de cursos y materias no genera aulas válidas.');
      setMessageType('error');
      return;
    }
    if (!form.idColegio) {
      setMessage('No se pudo determinar el colegio activo.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage(plannedPairs.length > 1 ? 'Creando aulas...' : 'Creando aula...');
    setMessageType('info');
    try {
      const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        codigoSuscripcion: '',
        cursoIds: form.cursoIds,
        materiaIds: form.materiaIds,
        idsAsistentes: form.idsAsistentes,
        idProfesor: form.idProfesor,
        cicloLectivo: Number(form.cicloLectivo) || currentYear,
        idColegio: form.idColegio,
        ...(form.cursoIds.length === 1 ? { idCurso: form.cursoIds[0] } : {}),
        ...(form.materiaIds.length === 1 ? { idMateria: form.materiaIds[0] } : {})
      };

      await aulaService.createAula(payload);
      setMessage('Aulas creadas correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'aulas' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el aula.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <AdminMenu
          activeId="aulas"
          user={user}
          featureFlags={featureFlags}
          baseItems={ADMIN_DASHBOARD_MENU_ITEMS}
          onNavigate={(item) => navigateAdminMenuItem(navigate, item)}
        />
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Alta de aula</h1>
            <p className="page-subtitle">Crea una o varias aulas para el colegio {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '900px' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-door-open" />
                <span>Datos del aula</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Completa los datos del aula, elige los cursos, las materias disponibles para esos cursos y el profesor responsable.
              </p>

              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Nombre base</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Aula 1"
                  />
                  <small style={{ color: '#64748b' }}>
                    Si en un mismo curso eliges más de una materia, el sistema agregará el nombre de la materia para diferenciar cada aula.
                  </small>
                </div>
                <div className="form-field">
                  <label>Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    placeholder="Descripción del aula"
                    style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                  />
                </div>
                <div className="form-field">
                  <label>Ciclo lectivo</label>
                  <input
                    type="number"
                    value={form.cicloLectivo}
                    onChange={(e) => handleCycleChange(e.target.value)}
                    min="2000"
                    max="2100"
                  />
                </div>
                <div className="form-field">
                  <label>Cursos del aula</label>
                  <SearchableSelect
                    value={form.cursoIds}
                    onChange={handleCursosChange}
                    options={cursoOptions}
                    placeholder="Busca cursos por año, división o nombre"
                    emptyText={`No hay cursos activos para el ciclo lectivo ${selectedCycle}.`}
                    noResultsText="No encontramos cursos con esa búsqueda."
                    disabled={isLoadingData}
                    multiple
                  />
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem', fontWeight: 600, color: '#0f172a' }}>
                      <input
                        type="checkbox"
                        checked={form.aTodosLosCursos}
                        onChange={(e) => handleToggleAllCourses(e.target.checked)}
                        disabled={isLoadingData}
                      />
                      Todos los cursos
                    </label>
                    <small style={{ color: '#64748b' }}>
                      Se seleccionarán automáticamente todos los cursos activos del ciclo lectivo {selectedCycle}.
                    </small>
                  </div>
                </div>
                <div className="form-field">
                  <label>Materias para los cursos elegidos</label>
                  <SearchableSelect
                    value={form.materiaIds}
                    onChange={handleMateriasChange}
                    options={materiaOptions}
                    placeholder={form.cursoIds.length > 0 ? 'Busca materias por nombre, código, área o curso' : 'Primero selecciona uno o más cursos'}
                    emptyText={form.cursoIds.length > 0 ? 'No hay materias disponibles para esos cursos.' : 'Selecciona cursos para ver las materias.'}
                    noResultsText="No encontramos materias con esa búsqueda."
                    disabled={isLoadingData || isLoadingMaterias || form.cursoIds.length === 0}
                    multiple
                  />
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem', fontWeight: 600, color: '#0f172a' }}>
                      <input
                        type="checkbox"
                        checked={form.aTodasLasMaterias}
                        onChange={(e) => handleToggleAllMaterias(e.target.checked)}
                        disabled={isLoadingData || isLoadingMaterias || form.cursoIds.length === 0}
                      />
                      Todas las materias
                    </label>
                    <small style={{ color: '#64748b' }}>
                      Aquí solo aparecen materias activas asociadas a los cursos seleccionados.
                    </small>
                    {plannedPairs.length > 0 && (
                      <small style={{ color: '#1d4ed8', fontWeight: 600 }}>
                        La selección actual generará {plannedPairs.length} {plannedPairs.length === 1 ? 'aula' : 'aulas'}.
                      </small>
                    )}
                    {coursesWithMultipleMaterias.length > 0 && (
                      <small style={{ color: '#b45309', fontWeight: 600 }}>
                        En estos cursos se crearán varias aulas diferenciadas por materia: {coursesWithMultipleMaterias.join(', ')}.
                      </small>
                    )}
                  </div>
                </div>
                <div className="form-field">
                  <label>Profesor responsable</label>
                  <SearchableSelect
                    value={form.idProfesor}
                    onChange={(value) => handleChange('idProfesor', value)}
                    options={profesorOptions}
                    placeholder="Busca un profesor por nombre o email"
                    emptyText="No hay profesores disponibles en este colegio."
                    noResultsText="No encontramos profesores con esa búsqueda."
                    disabled={isLoadingData}
                  />
                </div>
                {hasAgentesClasicos && (
                  <div className="form-field">
                    <label>Asistentes IA del aula</label>
                    <SearchableSelect
                      value={form.idsAsistentes}
                      onChange={(value) => handleChange('idsAsistentes', value)}
                      options={assistantOptions}
                      placeholder="Busca asistentes IA por nombre, materia o área"
                      emptyText="No hay asistentes IA finalizados disponibles."
                      noResultsText="No encontramos asistentes IA con esa búsqueda."
                      disabled={isLoadingData}
                      multiple
                    />
                    <small style={{ color: '#64748b' }}>
                      El aula mostrará únicamente los asistentes IA seleccionados aquí.
                    </small>
                  </div>
                )}

                {message && (
                  <div className={`form-message ${messageType}`}>
                    <span>{message}</span>
                  </div>
                )}

                <div className="form-actions">
                  <button className="btn btn-secondary" type="button" onClick={() => navigate('/admin-dashboard', { state: { tab: 'aulas' } })}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting
                      ? 'Guardando...'
                      : plannedPairs.length > 1
                        ? `Crear ${plannedPairs.length} aulas`
                        : 'Crear aula'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
      {(isLoadingData || isLoadingMaterias || isSubmitting) && (
        <LoadingSpinner
          message={
            isSubmitting
              ? 'Guardando aulas...'
              : isLoadingMaterias
                ? 'Obteniendo materias disponibles...'
                : 'Obteniendo datos...'
          }
        />
      )}
    </div>
  );
};

export default AdminCreateAula;
